import { useTranslation } from 'react-i18next';
import { Thumbnail } from '@/components';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { CameraCreationItem, CameraConnectionStatus } from '@/types/camera';
import { CameraMenuItem } from './CameraItem';

interface AddCameraModalSidebarProps {
    cameras: CameraCreationItem[];
    activeCameraId: string | null;
    onCameraSelect: (id: string) => void;
    onAddCamera: () => void;
    onDeleteCamera: (id: string) => void;
    connectionStatus: Record<string, CameraConnectionStatus>;
    addDisabled?: boolean;
    atCapHint?: string;
}

export function AddCameraModalSidebar({
    cameras,
    activeCameraId,
    onCameraSelect,
    onAddCamera,
    onDeleteCamera,
    connectionStatus,
    addDisabled = false,
    atCapHint,
}: AddCameraModalSidebarProps) {
    const { t } = useTranslation(I18nNamespace.CAMERAS);
    const canDeleteCameras = cameras.length > 1;

    const addCameraButton = (
        <div
            className={
                addDisabled
                    ? 'group flex cursor-not-allowed items-center gap-2 rounded-[8px] p-1.5 opacity-50'
                    : 'group flex cursor-pointer items-center gap-2 rounded-[8px] p-1.5 transition-colors hover:bg-grey-600'
            }
            onClick={() => {
                if (!addDisabled) onAddCamera();
            }}
            onKeyDown={(keyboardEvent) => {
                if (
                    keyboardEvent.key === 'Enter' ||
                    keyboardEvent.key === ' '
                ) {
                    keyboardEvent.preventDefault();
                    if (!addDisabled) onAddCamera();
                }
            }}
            role="button"
            tabIndex={addDisabled ? -1 : 0}
            aria-disabled={addDisabled}
            data-testid="cameras-add"
        >
            <Thumbnail
                variant="add"
                compact
                visualStyle="dashed"
                className="shrink-0 border-grey-500 group-hover:border-grey-400"
            />
            <div className="min-w-0 flex-1">
                <p className="text-xs font-normal text-grey-200">
                    {t('modal.addCamera')}
                </p>
            </div>
        </div>
    );

    return (
        <div className="w-72 flex-shrink-0 flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-3 py-4 scrollbar-hide">
                <div className="flex flex-col">
                    {cameras.map((camera) => (
                        <CameraMenuItem
                            key={camera.id}
                            camera={camera}
                            isActive={camera.id === activeCameraId}
                            onSelect={() => onCameraSelect(camera.id)}
                            onDelete={() => onDeleteCamera(camera.id)}
                            canDelete={canDeleteCameras}
                            connectionStatus={connectionStatus[camera.id]}
                        />
                    ))}

                    {addDisabled && atCapHint ? (
                        <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                                {addCameraButton}
                            </TooltipTrigger>
                            <TooltipContent
                                side="right"
                                sideOffset={8}
                                className="z-[60] max-w-xs"
                                data-testid="cameras-at-cap-hint"
                            >
                                {atCapHint}
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        addCameraButton
                    )}
                </div>
            </div>
        </div>
    );
}
